import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, Brain } from "lucide-react";
import { toast } from "sonner";
import { Trans, useLingui } from "@lingui/macro";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import PromptStackManager, {
  type PromptStackConfig,
  type PromptStackWrite,
  type LlmApp,
} from "@app/components/PromptStackManager";
import API from "@app/services/api";
import { useLabReadOnly } from "@app/utils/use-lab-read-only";

/**
 * Laboratoire — Modèle LLM bento.
 *
 * Surface CRUD for ``PromptStack`` rows (the LLM pillar): the registry app
 * (``llm_app_key``), temperature, system prompt, reasoning toggle, and the
 * few-shot strategy. The served model + endpoint URL are derived from the
 * chosen app. Reuses ``PromptStackManager`` from the dataset-scoped view.
 */
export default function LaboratoireLlmPage() {
  const { t } = useLingui();
  const labReadOnly = useLabReadOnly();

  const [configs, setConfigs] = useState<PromptStackConfig[]>([]);
  const [llmApps, setLlmApps] = useState<LlmApp[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get({ path: "/prompt-stack/" });
      if (res.ok && res.data?.promptStacks) {
        setConfigs(res.data.promptStacks as PromptStackConfig[]);
      } else if (!res.ok) {
        toast.error(res.error || t`Erreur lors du chargement`);
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Registry LLM apps power the editor's app selector. The backend restricts
  // this to admins and MAGE-X reviewers; other roles simply get an empty list.
  const loadLlmApps = useCallback(async () => {
    const res = await API.get({ path: "/prompt-stack/llm-apps" });
    if (res.ok && res.data?.llmApps) {
      setLlmApps(res.data.llmApps as LlmApp[]);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadLlmApps();
  }, [load, loadLlmApps]);

  const handleAdd = async (config: PromptStackWrite) => {
    const res = await API.post({ path: "/prompt-stack/", body: config });
    if (!res.ok) {
      toast.error(res.error || t`Erreur lors de la création`);
      return;
    }
    await load();
  };

  const handleUpdate = async (
    configId: string,
    updates: Partial<PromptStackWrite>,
  ) => {
    const res = await API.put({
      path: `/prompt-stack/${configId}`,
      body: updates,
    });
    if (!res.ok) {
      toast.error(res.error || t`Erreur lors de la mise à jour`);
      return;
    }
    await load();
  };

  const handleDelete = async (configId: string) => {
    const res = await API.delete({ path: `/prompt-stack/${configId}` });
    if (!res.ok) {
      toast.error(res.error || t`Erreur lors de la suppression`);
      return;
    }
    await load();
  };

  return (
    <div className="p-6">
      <Link
        to="/laboratoire"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        <Trans>Laboratoire</Trans>
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Brain className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            <Trans>Modèle LLM</Trans>
          </h1>
          <p className="text-slate-600 mt-1">
            <Trans>
              Configurations LLM réutilisables — app du registre, température,
              prompt système, mode raisonnement, et stratégie de sélection des
              exemples (RAG, statique, ou aucun). Le modèle et l'URL d'inférence
              sont dérivés de l'app choisie. Chaque expérience référence l'une
              de ces configurations.
            </Trans>
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <Trans>Configurations</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PromptStackManager
            configs={configs}
            llmApps={llmApps}
            onAdd={handleAdd}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            loading={loading}
            readOnly={labReadOnly}
          />
        </CardContent>
      </Card>
    </div>
  );
}
